import { Copy01Icon } from '@hugeicons-pro/core-stroke-rounded';
import Clipboard from '@react-native-clipboard/clipboard';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, Platform } from 'react-native';

import { Menu, MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useToastMessages } from '@/hooks/useToastMessages';
import dayjs from '@/i18n/dayjs-config';
import { ConversationItem } from '@/models/Conversation';

interface ConversationBubbleContextMenuProps {
  item: ConversationItem;
  children: React.ReactNode;
}

export function ConversationBubbleContextMenu({
  item,
  children,
}: ConversationBubbleContextMenuProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const isLargeScreen = useIsLargeScreen();

  const handleCopy = async () => {
    try {
      // Only copy text content
      if (!item.Text) {
        showErrorToast(t('messages.copy.noContent'));
        return;
      }

      Clipboard.setString(item.Text);
      showSuccessToast(t('messages.copy.success'));
    } catch (error) {
      showErrorToast(t('messages.copy.error'), error);
    }
  };

  // Format the date using dayjs with llll format for full localized date and time
  // Use the same timezone handling pattern as other components
  const dateObj =
    typeof item.DateCreated === 'string'
      ? dayjs(
          (item.DateCreated as string).endsWith('Z')
            ? (item.DateCreated as string)
            : (item.DateCreated as string) + 'Z'
        )
      : dayjs(item.DateCreated);
  const formattedDateTime = dateObj.format('llll');

  return (
    <Menu
      placement="bottom left"
      offset={8}
      trigger={(props: any) => (
        <Pressable
          {...props}
          className="select-none self-start"
          onPress={undefined}
          onLongPress={!isLargeScreen ? props.onPress : undefined}
          onContextMenu={
            Platform.OS === 'web'
              ? (e: any) => {
                  e.preventDefault();
                  props.onPress();
                }
              : undefined
          }>
          {children}
        </Pressable>
      )}>
      {/* Show date and time as a non-interactive menu item */}
      <MenuItem
        key="datetime"
        textValue={formattedDateTime}
        disabled
        className="bg-transparent data-[active=true]:bg-transparent data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent">
        <MenuItemLabel className="text-xs text-typography-500">{formattedDateTime}</MenuItemLabel>
      </MenuItem>
      {item.Text && (
        <MenuItem key="copy" textValue={t('post.share.copy')} onPress={handleCopy}>
          <MenuItemIcon icon={Copy01Icon} />
          <MenuItemLabel>{t('post.share.copy')}</MenuItemLabel>
        </MenuItem>
      )}
    </Menu>
  );
}
